import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Switch,
  Heading,
  Icon,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaPlus, FaEdit, FaTrash, FaEye, FaStore, FaBoxOpen } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

// سرویس‌های واقعی بک‌اند
import { getGateways, deleteGateway, updateGateway } from '../services/gatewayService';

// کامپوننت‌های فرم و مودال شما
import CustomModal from '../components/Modal'; // مسیر ایمپورت خود را در صورت نیاز اصلاح کنید
import GatewayForm from '../components/forms/GatewayForm';

export default function Gateways() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 🎨 متغیرهای رنگی برای تم روشن/تاریک (SaaS Design)
  const bgPage = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const theadBg = useColorModeValue("gray.50", "gray.700");
  const searchBarBg = useColorModeValue("white", "gray.800");
  const searchInputBg = useColorModeValue("gray.50", "gray.900");
  const searchInputFocusBg = useColorModeValue("white", "gray.800");

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const formRef = useRef(null);
  
  // ==========================================
  // دریافت لیست درگاه‌ها (Queries)
  // ==========================================
  const {
    data: gatewaysData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['gateways'],
    getGateways,
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  // ==========================================
  // عملیات حذف (Mutations)
  // ==========================================
  const deleteGatewayMutation = useMutation(deleteGateway, {
    onSuccess: () => {
      toast.success('درگاه با موفقیت حذف شد');
      queryClient.invalidateQueries(['gateways']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'خطا در حذف درگاه');
    }
  });

  const handleDeleteGateway = (gatewayId) => {
    if (window.confirm('آیا از حذف کامل این درگاه و قطع ارتباط محصولات آن اطمینان دارید؟')) {
      deleteGatewayMutation.mutate(gatewayId);
    }
  };

  // ==========================================
  // هندلرهای مودال و فرم
  // ==========================================
  const handleAddGateway = () => {
    setEditingGateway(null);
    setIsFormDirty(false);
    setIsModalOpen(true);
  };

  const handleEditGateway = (gateway) => {
    setEditingGateway(gateway);
    setIsFormDirty(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingGateway(null);
    setIsFormDirty(false);
  };

  const handleSubmitForm = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  // ==========================================
  // فیلتر جستجو
  // ==========================================
  const filteredGateways = gatewaysData?.data?.filter(gateway => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      gateway.name?.toLowerCase().includes(searchLower) ||
      gateway.type?.toLowerCase().includes(searchLower)
    );
  }) || [];

  // ==========================================
  // وضعیت بارگذاری (Loading State)
  // ==========================================
  if (isLoading) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} align="center" justify="center">
        <Box position="relative" mb={6}>
          <Spinner size="xl" thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" w="60px" h="60px" />
          <Icon as={FaStore} position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" color="blue.500" />
        </Box>
        <Text fontSize="lg" fontWeight="bold" color={textColor} animation="pulse">
          در حال دریافت لیست فروشگاه‌ها...
        </Text>
      </Flex>
    );
  }

  // ==========================================
  // وضعیت خطا (Error State)
  // ==========================================
  if (error) {
    return (
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} p={8}>
        <Alert status="error" borderRadius="2xl" boxShadow="sm" p={6}>
          <AlertIcon boxSize="24px" />
          <Box ml={4}>
            <AlertTitle fontSize="lg" mb={2}>خطا در بارگذاری داده‌ها!</AlertTitle>
            <AlertDescription color="gray.600">
              {error.message || 'مشکلی در ارتباط با سرور به وجود آمده است.'}
            </AlertDescription>
          </Box>
        </Alert>
        <Button mt={6} colorScheme="blue" alignSelf="start" onClick={() => refetch()} isLoading={isLoading}>
          تلاش مجدد
        </Button>
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" dir="rtl" minH="100vh" bg={bgPage} pb={10}>
        <Box maxW="7xl" mx="auto" w="full" px={{ base: 4, md: 8 }} py={8}>
          
          {/* هدر صفحه */}
          <Flex justify="space-between" align="flex-end" mb={8} flexWrap="wrap" gap={4}>
            <Box>
              <Heading size="lg" color={textColor} letterSpacing="tight" mb={2}>
                مدیریت درگاه‌های فروش
              </Heading>
              <Text fontSize="sm" color={mutedColor}>
                فروشگاه‌های دیجی‌کالا، باسلام و سایت شخصی خود را متصل و مدیریت کنید.
              </Text>
            </Box>
            <Button
              leftIcon={<FaPlus />}
              bgGradient="linear(to-r, blue.500, cyan.400)"
              color="white"
              borderRadius="xl"
              px={6}
              py={6}
              fontSize="md"
              fontWeight="bold"
              boxShadow="0 10px 20px -5px rgba(59,130,246,0.4)"
              _hover={{
                bgGradient: 'linear(to-r, blue.600, cyan.500)',
                transform: 'translateY(-2px)',
                boxShadow: '0 15px 25px -5px rgba(59,130,246,0.5)',
              }}
              _active={{ transform: 'translateY(0)' }}
              onClick={handleAddGateway}
            >
              افزودن درگاه جدید
            </Button>
          </Flex>

          {/* بدنه اصلی (کارت جدول) */}
          <Box bg={cardBg} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm" overflow="hidden">
            
            {/* نوار جستجو */}
            <Flex p={5} borderBottomWidth="1px" borderColor={borderColor} bg={searchBarBg}>
              <InputGroup maxW="400px" size="md">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="جستجو با نام یا نوع درگاه..."
                  borderRadius="xl"
                  bg={searchInputBg}
                  border="none"
                  _focus={{ ring: 2, ringColor: "blue.400", bg: searchInputFocusBg }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Flex>

            {/* جدول درگاه‌ها */}
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg={theadBg}>
                  <Tr>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>نام فروشگاه</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>نوع اتصال</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>قیمت‌گذاری خودکار</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor}>وضعیت فعالیت</Th>
                    <Th color={mutedColor} fontSize="xs" textTransform="none" py={4} borderColor={borderColor} textAlign="center">عملیات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredGateways.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center" py={16} borderBottom="none">
                        <Flex direction="column" align="center" justify="center">
                          <Icon as={FaBoxOpen} boxSize={12} color="gray.300" mb={4} />
                          <Text fontSize="lg" fontWeight="bold" color={textColor} mb={1}>
                            {searchTerm ? 'درگاهی با این نام یافت نشد' : 'هیچ درگاهی ثبت نشده است'}
                          </Text>
                          <Text fontSize="sm" color={mutedColor}>
                            برای شروع، روی دکمه «افزودن درگاه جدید» کلیک کنید.
                          </Text>
                        </Flex>
                      </Td>
                    </Tr>
                  ) : (
                    filteredGateways.map((gateway) => (
                      <Tr 
                        key={gateway._id} 
                        _hover={{ bg: hoverBg }} 
                        transition="background 0.2s"
                      >
                        <Td borderColor={borderColor} py={4}>
                          <Link to={`/gateways/${gateway._id}`}>
                            <HStack spacing={3}>
                              <Flex w="40px" h="40px" borderRadius="lg" bg="blue.50" color="blue.500" align="center" justify="center">
                                <Icon as={FaStore} />
                              </Flex>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color={textColor}
                                _hover={{ color: "blue.500" }}
                                transition="color 0.2s"
                              >
                                {gateway.name}
                              </Text>
                            </HStack>
                          </Link>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge
                            bg={
                              gateway.type === 'basalam' ? 'orange.500' :
                              gateway.type === 'digikala' ? 'red.500' : 'purple.500'
                            }
                            color="white"
                            fontSize="xs"
                            px={3}
                            py={1}
                            borderRadius="full"
                            shadow="sm"
                          >
                            {gateway.type === 'basalam' ? 'باسلام' :
                             gateway.type === 'digikala' ? 'دیجی‌کالا' : 'سایت شخصی'}
                          </Badge>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Tooltip 
                            label={gateway.syncPrice === false ? "قیمت‌گذاری خودکار برای این درگاه غیرفعال است (قیمت محصولات دست‌نخورده می‌ماند)" : "قیمت‌گذاری خودکار محصولات فعال است"} 
                            placement="top" 
                            hasArrow
                          >
                            <HStack spacing={2}>
                              <Switch
                                isChecked={gateway.syncPrice !== false}
                                colorScheme="purple"
                                size="md"
                                onChange={async (e) => {
                                  try {
                                    await updateGateway(gateway._id, { syncPrice: e.target.checked });
                                    queryClient.invalidateQueries(['gateways']);
                                    toast.success(e.target.checked ? 'قیمت‌گذاری خودکار فعال شد' : 'قیمت‌گذاری خودکار این درگاه غیرفعال شد');
                                  } catch (err) {
                                    toast.error(err?.response?.data?.message || 'خطا در تغییر وضعیت قیمت‌گذاری');
                                  }
                                }}
                              />
                              {gateway.syncPrice === false && (
                                <Badge colorScheme="purple" variant="subtle" fontSize="9px" borderRadius="md">
                                  غیرفعال
                                </Badge>
                              )}
                            </HStack>
                          </Tooltip>
                        </Td>
                        <Td borderColor={borderColor}>
                          <Switch
                            isChecked={gateway.isActive !== false}
                            colorScheme="green"
                            size="md"
                            onChange={async (e) => {
                              try {
                                await updateGateway(gateway._id, { isActive: e.target.checked });
                                queryClient.invalidateQueries(['gateways']);
                                toast.success('وضعیت با موفقیت بروزرسانی شد');
                              } catch (err) {
                                toast.error(err?.response?.data?.message || 'خطا در تغییر وضعیت');
                              }
                            }}
                          />
                        </Td>
                        <Td borderColor={borderColor} textAlign="center">
                          <HStack spacing={2} justify="center">
                            <Tooltip label="مشاهده پروفایل" placement="top" hasArrow>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                icon={<FaEye />}
                                onClick={() => navigate(`/gateways/${gateway._id}`)}
                                borderRadius="lg"
                              />
                            </Tooltip>
                            <Tooltip label="ویرایش درگاه" placement="top" hasArrow>
                              <IconButton
                                size="sm"
                                variant="ghost"
                                colorScheme="teal"
                                icon={<FaEdit />}
                                onClick={() => handleEditGateway(gateway)}
                                borderRadius="lg"
                              />
                            </Tooltip>
                            <Tooltip label="حذف درگاه" placement="top" hasArrow bg="red.500">
                              <IconButton
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                icon={<FaTrash />}
                                onClick={() => handleDeleteGateway(gateway._id)}
                                borderRadius="lg"
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* ========================================== */}
      {/* Modal برای افزودن/ویرایش درگاه */}
      {/* ========================================== */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingGateway ? 'ویرایش اطلاعات درگاه' : 'ایجاد درگاه فروش جدید'}
        onSubmit={handleSubmitForm}
        submitText={editingGateway ? "ذخیره تغییرات" : "ثبت و ایجاد درگاه"}
        isDirty={isFormDirty}
      >
        <GatewayForm
          ref={formRef}
          gateway={editingGateway}
          onDirty={() => setIsFormDirty(true)}
          onSuccess={(createdGateway) => {
            queryClient.invalidateQueries(['gateways']);
            // اگر درگاه دیجی‌کالا تازه اضافه شده، مودال باز بماند برای دریافت توکن
            if (createdGateway && createdGateway.type === 'digikala') {
              setEditingGateway(createdGateway);
            } else {
              setIsModalOpen(false);
              setEditingGateway(null);
            }
          }}
        />
      </CustomModal>
    </>
  );
}